import { Link } from "react-router-dom";
import { UpcomingMatch } from "../models/UpcomingMatch";
import { FC, useState, useMemo, useEffect } from "react";
import styles from '../styles/Tables.module.css';
import buttonStyles from '../styles/MatchButton.module.css';
import { parseMatchTime } from '../utils/matchTime';
import { useTranslation } from "react-i18next";
import { LiveMatch, orientToTeam1, useLiveMatchLookup } from "../services/LiveService";
import LiveBadge from "../pages/LivePage/LiveBadge";
import liveStyles from "../pages/LivePage/Live.module.css";

interface UpcomingMatchTableProps {
  matchesForDate: UpcomingMatch[];
  season?: number; // Make season optional with '?'
  isMobile?: boolean; // Add optional mobile flag
  showDivision?: boolean; // Show division badge next to time
}

// Helper function to get short division badge text
const getDivisionBadge = (division: string) => {
  if (division === 'ligaen') return 'L';
  if (division === '1div') return '1D';
  if (division === '2divA') return '2A';
  if (division === '2divB') return '2B';
  if (division === '3divA') return '3A';
  if (division === '3divB') return '3B';
  if (division === '3divC') return '3C';
  if (division === '3divD') return '3D';
  return division.substring(0, 2).toUpperCase();
};

// LIVE/Færdig pill over the running score, in the row's team order (the feed can list the
// teams the other way round).
const LiveScore: FC<{ live: LiveMatch; team1Id: string }> = ({ live, team1Id }) => {
  const [a, b] = orientToTeam1(live, team1Id);
  return (
    <div className={liveStyles.rowLive}>
      <LiveBadge finished={live.finished} />
      <span className={liveStyles.rowScore}>{a.score} : {b.score}</span>
    </div>
  );
};

const UpcomingMatchTable: FC<UpcomingMatchTableProps> = ({ matchesForDate, season, isMobile: propIsMobile, showDivision }) => {
  const [isMobile, setIsMobile] = useState(propIsMobile || window.innerWidth < 768);
  const { t } = useTranslation("pages");
  // Maps OverPass is streaming right now, keyed by the same id as challonge_id.
  const liveMatches = useLiveMatchLookup();

  // Setup mobile detection if not provided via props
  useEffect(() => {
    if (propIsMobile !== undefined) {
      setIsMobile(propIsMobile);
      return;
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [propIsMobile]);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending';
  }>({
    key: '',
    direction: 'ascending',
  });

  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedMatches = useMemo(() => {
    const sortedData = [...matchesForDate];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'team1':
            aValue = a.team1.teamName || '';
            bValue = b.team1.teamName || '';
            break;
          case 'team2':
            aValue = a.team2.teamName || '';
            bValue = b.team2.teamName || '';
            break;
          case 'match_time':
            aValue = parseMatchTime(a.match_time).getTime();
            bValue = parseMatchTime(b.match_time).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedData;
  }, [matchesForDate, sortConfig]);

  // Helper function to build team link (with or without ?season=...)
  const buildTeamLink = (teamId: string) => {
    if (season !== undefined) {
      return `/team/${teamId}?season=${season}`;
    }
    return `/team/${teamId}`;
  };

  return (
    <div className={`${styles.tableContainer} ${styles.dark}`}>
      <table className={`${styles.table} ${isMobile ? styles.compact : ''}`}>
        <thead>
          <tr className={`${styles.tableHeader} ${styles.dark}`}>
            <th
              className={`${styles.tableHeaderCell} ${styles.dark}`}
              style={{ width: '35%' }}
              onClick={() => handleSort('team1')}
            >
              Hold 1{" "}
              {sortConfig.key === 'team1' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th
              className={`${styles.tableHeaderCell} ${styles.center} ${styles.dark}`}
              style={{ width: '30%' }}
              onClick={() => handleSort('match_time')}
            >
              VS{" "}
              {sortConfig.key === 'match_time' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th
              className={`${styles.tableHeaderCell} ${styles.dark}`}
              style={{ width: '35%' }}
              onClick={() => handleSort('team2')}
            >
              Hold 2{" "}
              {sortConfig.key === 'team2' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedMatches.map((match, index) => (
            <tr 
              key={index} 
              className={`${styles.tableRow} ${styles.standard} ${styles.dark} ${isMobile ? styles.mobile : ''}`}
            >
              <td className="align-middle" style={{ padding: isMobile ? '0.5rem' : undefined }}>
                {/* Link includes ?season=... only if season is passed */}
                <Link
                  to={buildTeamLink(match.team1.teamId)}
                  className="text-decoration-none text-light d-flex flex-column align-items-center"
                >
                  <img
                    src={(() => {
                      const logoUrl = match.team1.teamLogo;
                      if (!logoUrl) return '';
                      // Determine the desired width based on display mode
                      const desiredWidth = isMobile ? 60 : 80;
                      // Add width parameter to URL if it doesn't already have one
                      if (logoUrl.includes('getTeamLogos.php') && !logoUrl.includes('width=')) {
                        return `${logoUrl}&width=${desiredWidth}`;
                      }
                      return logoUrl;
                    })()}
                    alt={match.team1.teamName}
                    style={{ 
                      width: isMobile ? 30 : 40, 
                      height: isMobile ? 30 : 40, 
                      objectFit: 'contain' 
                    }}
                    loading="lazy"
                  />
                  <span
                    className="mt-1 text-truncate"
                    style={{ maxWidth: '100%' }}
                  >
                    {match.team1.teamName}
                  </span>
                </Link>
              </td>
              <td className="align-middle text-center" style={{
                fontSize: isMobile ? '1.2rem' : '1.5rem',
                padding: isMobile ? '0.5rem' : undefined
              }}>
                {liveMatches[match.challonge_id] ? (
                  // Live: the running score instead of the kick-off time, and a button to the
                  // live scoreboard instead of PreMatchPage.
                  <>
                    <LiveScore live={liveMatches[match.challonge_id]} team1Id={match.team1.teamId} />
                    <Link
                      to={`/live/${match.challonge_id}`}
                      className={`${buttonStyles.matchButton} ${isMobile ? buttonStyles.small : ''}`}
                    >
                      {t("live.watchLive")}
                    </Link>
                  </>
                ) : (
                  <>
                    <div>
                      {parseMatchTime(match.match_time).toLocaleTimeString('da-DK', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                      {showDivision && match.division && (
                        <span
                          className="badge bg-secondary ms-2"
                          style={{ fontSize: '0.6rem', verticalAlign: 'middle' }}
                          title={match.division}
                        >
                          {getDivisionBadge(match.division)}
                        </span>
                      )}
                    </div>
                    {/* Button to navigate to PreMatchPage */}
                    <Link
                      to={`/prematch/${match.challonge_id}`}
                      className={`${buttonStyles.matchButton} ${isMobile ? buttonStyles.small : ''}`}
                    >
                      Se kamp
                    </Link>
                  </>
                )}
              </td>
              <td className="align-middle" style={{ padding: isMobile ? '0.5rem' : undefined }}>
                {/* Link includes ?season=... only if season is passed */}
                <Link
                  to={buildTeamLink(match.team2.teamId)}
                  className="text-decoration-none text-light d-flex flex-column align-items-center"
                >
                  <img
                    src={(() => {
                      const logoUrl = match.team2.teamLogo;
                      if (!logoUrl) return '';
                      // Determine the desired width based on display mode
                      const desiredWidth = isMobile ? 60 : 80;
                      // Add width parameter to URL if it doesn't already have one
                      if (logoUrl.includes('getTeamLogos.php') && !logoUrl.includes('width=')) {
                        return `${logoUrl}&width=${desiredWidth}`;
                      }
                      return logoUrl;
                    })()}
                    alt={match.team2.teamName}
                    style={{ 
                      width: isMobile ? 30 : 40, 
                      height: isMobile ? 30 : 40, 
                      objectFit: 'contain' 
                    }}
                    loading="lazy"
                  />
                  <span
                    className="mt-1 text-truncate"
                    style={{ maxWidth: '100%' }}
                  >
                    {match.team2.teamName}
                  </span>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UpcomingMatchTable;